import React, { useState } from "react";
import { Copy, Plus, X } from "lucide-react";
import { WEEKDAYS, dayName } from "@/lib/customCalc";
import { ManualForm } from "./ItemAddTabs";

const R = { rice: /rice/i, dal: /dal|pulse/i, veg: /^veg|vegetable|sabzi/i, oil: /oil/i, salt: /masala|salt|condiment/i, fuel: /fuel/i,
  soya: /soya/i, egg: /egg/i, wheat: /wheat|atta|roti|flour/i, khichdi: /khichdi/i };
const TEMPLATES = [
  ["Rice + Dal + Veg", ["rice", "dal", "veg", "oil", "salt", "fuel"]],
  ["Khichdi", ["khichdi", "rice", "dal", "veg", "oil", "salt", "fuel"]],
  ["Rice + Soya curry", ["rice", "soya", "veg", "oil", "salt", "fuel"]],
  ["Roti + Sabzi", ["wheat", "veg", "oil", "salt", "fuel"]],
  ["Rice + Egg curry", ["rice", "egg", "veg", "oil", "salt", "fuel"]],
];

export default function StepMenu({ cfg, set, b, bl }) {
  const [same, setSame] = useState(false);
  const [copyTo, setCopyTo] = useState([]);
  const [adding, setAdding] = useState(false);
  const order = cfg.items.map((i) => i.key);
  const sortK = (ks) => [...new Set(ks)].sort((a, c) => order.indexOf(a) - order.indexOf(c));
  const upd = (wd, patch) => set((c) => {
    const targets = same && patch.serving === undefined ? Object.keys(c.menu).filter((k) => c.menu[k].serving) : [wd];
    const menu = { ...c.menu };
    targets.forEach((k) => { menu[k] = { ...menu[k], ...patch }; });
    return { ...c, menu };
  });
  const toggleKeys = (wd, keys) => {
    const cur = cfg.menu[wd].items || [];
    const on = keys.every((k) => cur.includes(k));
    upd(wd, { items: sortK(on ? cur.filter((k) => !keys.includes(k)) : [...cur, ...keys]) });
  };
  const applyTpl = (wd, name) => {
    const t = TEMPLATES.find((x) => x[0] === name);
    if (!t) return;
    const keys = t[1].map((r) => cfg.items.find((i) => !i.group && (R[r].test(i.key) || R[r].test(i.name)))?.key).filter(Boolean);
    upd(wd, { items: sortK(keys), display: name });
  };
  const copyMonday = () => set((c) => {
    const src = c.menu["1"];
    const menu = { ...c.menu };
    copyTo.forEach((k) => { menu[k] = { ...src, items: [...(src.items || [])] }; });
    return { ...c, menu };
  });
  const loose = cfg.items.filter((i) => !i.group);
  const groups = (cfg.groups || []).map((g) => ({ ...g, keys: cfg.items.filter((i) => i.group === g.key).map((i) => i.key) })).filter((g) => g.keys.length);

  return (
    <div className="space-y-3" data-testid="step-menu">
      <div className="rounded-2xl bg-thali-cream/60 border border-thali-turmeric/30 p-3 space-y-3">
        <label className="flex items-center gap-2 text-sm font-semibold min-h-[40px]">
          <input type="checkbox" className="w-5 h-5 accent-thali-green" checked={same} onChange={(e) => setSame(e.target.checked)} data-testid="b-menu-same" /> {b("same_menu")}
        </label>
        <div>
          <div className="text-xs font-semibold mb-1">{b("copy_monday")}</div>
          <div className="flex flex-wrap gap-1.5">
            {[2, 3, 4, 5, 6, 0].map((n) => {
              const on = copyTo.includes(String(n));
              return <button key={n} onClick={() => setCopyTo((s) => (on ? s.filter((x) => x !== String(n)) : [...s, String(n)]))}
                className={`text-xs font-bold rounded-full px-3 py-2 border-2 ${on ? "bg-thali-green text-white border-thali-green" : "bg-white border-thali-turmeric/30"}`} data-testid={`b-copymon-${n}`}>{dayName(n, bl).slice(0, 3)}</button>;
            })}
            <button onClick={copyMonday} disabled={!copyTo.length} className="text-xs font-bold rounded-full px-3 py-2 bg-thali-saffron text-white disabled:opacity-40" data-testid="b-copymon-apply"><Copy className="w-3 h-3 inline" /> {b("apply")}</button>
          </div>
        </div>
        <button onClick={() => setAdding((a) => !a)} className="text-sm font-bold text-thali-green inline-flex items-center gap-1" data-testid="b-menu-add-item">{adding ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {b("add_new_item")}</button>
        {adding && <ManualForm cfg={cfg} set={set} b={b} onDone={() => setAdding(false)} />}
      </div>
      {WEEKDAYS.map((n) => {
        const wd = String(n);
        const m = cfg.menu[wd] || { serving: false, items: [] };
        const its = m.items || [];
        return (
          <div key={wd} className={`rounded-2xl border p-3 transition-colors ${m.serving ? "bg-white border-thali-turmeric/40" : "bg-thali-ink/5 border-thali-ink/10"}`} data-testid={`b-menu-${wd}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-heading font-bold">{dayName(n, bl)}</span>
              <div className="flex rounded-full bg-thali-cream p-1 text-xs font-bold">
                <button onClick={() => upd(wd, { serving: true })} className={`px-3 py-1.5 rounded-full ${m.serving ? "bg-thali-green text-white" : "text-thali-ink/60"}`} data-testid={`b-menu-serve-${wd}`}>🍛 {b("serving")}</button>
                <button onClick={() => upd(wd, { serving: false })} className={`px-3 py-1.5 rounded-full ${!m.serving ? "bg-thali-ink text-white" : "text-thali-ink/60"}`} data-testid={`b-menu-off-${wd}`}>{b("no_meal")}</button>
              </div>
            </div>
            {m.serving && (
              <div className="mt-3 space-y-2">
                <select className="input-thali h-10 text-sm" value="" onChange={(e) => applyTpl(wd, e.target.value)} data-testid={`b-menu-tpl-${wd}`}>
                  <option value="">📋 {b("menu_template")}</option>{TEMPLATES.map(([t]) => <option key={t} value={t}>{t}</option>)}
                </select>
                <div className="flex flex-wrap gap-2">
                  {loose.map((it) => (
                    <button key={it.key} onClick={() => toggleKeys(wd, [it.key])}
                      className={`min-h-[40px] text-sm rounded-full px-3 border-2 font-semibold transition-colors ${its.includes(it.key) ? "bg-thali-saffron/15 border-thali-saffron text-thali-ink" : "bg-white border-thali-turmeric/30 text-thali-ink/50"}`}
                      data-testid={`b-menu-item-${wd}-${it.key}`}>{it.emoji} {it.name || "—"}</button>
                  ))}
                  {groups.map((g) => (
                    <button key={g.key} onClick={() => toggleKeys(wd, g.keys)}
                      className={`min-h-[40px] text-sm rounded-full px-3 border-2 font-semibold transition-colors ${g.keys.every((k) => its.includes(k)) ? "bg-[#FFF4E6] border-thali-saffron text-thali-ink" : "bg-white border-dashed border-thali-turmeric/50 text-thali-ink/50"}`}
                      data-testid={`b-menu-group-${wd}-${g.key}`}>{g.emoji} {g.name} ({g.keys.length})</button>
                  ))}
                </div>
                <input className="input-thali" placeholder={b("menu_display")} value={m.display || ""} maxLength={100}
                  onChange={(e) => upd(wd, { display: e.target.value })} data-testid={`b-menu-display-${wd}`} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
